import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpc, trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { HubShelfBoxes } from 'shared/components/hub-shelf-boxes'
import { SectionTitle } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from 'shared/components/ui/tooltip'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'

export type HubShelfItem = RouterOutputs['discrepancies']['HubShelf'][number]
export type DecideInput = RouterInputs['discrepancies']['DecideHubShelf']
export type DecideAnswer = RouterOutputs['discrepancies']['DecideHubShelf']
export type ShelfPoint = RouterOutputs['buyerReturns']['Points'][number]

/** Support's decision on a Parcel of the hub shelf: decided, or refused with its reason; throws on a failed call. */
export type DecideOnHubShelf = (input: DecideInput) => Promise<DecideAnswer>

type Decision = DecideInput['decision']

const decideViaTrpc: DecideOnHubShelf = (input) => trpcClient.discrepancies.DecideHubShelf.mutate(input)
const pointsViaTrpc = () => trpcClient.buyerReturns.Points.query()

const SELECT_CLASS =
	'h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'

/**
 * «Полка хаба» in support's section (spec §19, user story 118; tickets 44, 45): every
 * Parcel the hub cannot send onward, with why it waits, and «Решить» where its fate is
 * support's — «В пункт» or «Вернуть партнёру», with a comment, in a dialog. Where it
 * is not support's (a refused line, a return, a found Parcel waiting for its partner,
 * a closed Point) the row says what it waits for. Below — a closed Point's whole Boxes.
 */
export function HubShelfDecisions() {
	const { t } = useTranslate()
	const query = useQuery(trpc.discrepancies.HubShelf.queryOptions())
	const boxes = useQuery(trpc.discrepancies.HubShelfBoxes.queryOptions())

	return (
		<div className='flex flex-col gap-8'>
			<section className='flex flex-col gap-2'>
				<SectionTitle title={t('support.hubShelfParcels')} count={query.data?.length} hint={t('support.hubShelfExplain')} />
				{query.isError ? (
					<p className='text-sm text-destructive'>{query.error.message}</p>
				) : query.isLoading ? (
					<p className='text-sm text-muted-foreground'>{t('support.loading')}</p>
				) : (
					<HubShelfTable
						items={query.data ?? []}
						decide={decideViaTrpc}
						listPoints={pointsViaTrpc}
						onDone={() => query.refetch()}
					/>
				)}
			</section>
			<HubShelfBoxes items={boxes.data ?? []} title={t('support.hubShelfBoxesTitle')} hint={t('support.hubShelfBoxesHint')} />
		</div>
	)
}

/** What a Parcel whose fate is not support's waits for — a short word, the reason on hover. */
function NotSupports({ item }: { item: HubShelfItem }) {
	const { t } = useTranslate()
	const [word, why] = item.reasons.includes('found_lost')
		? (['hubShelfDecision.waitsPartner', 'hubShelfDecision.foundLost'] as const)
		: item.reasons.includes('point_closed')
			? (['hubShelfDecision.waitsPoint', 'hubShelfDecision.pointClosed'] as const)
			: (['hubShelfDecision.goesToPartner', 'hubShelfDecision.toPartnerAlready'] as const)
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className='text-muted-foreground' data-testid='hub-shelf-not-support'>
					{t(word)}
				</span>
			</TooltipTrigger>
			<TooltipContent>{t(why)}</TooltipContent>
		</Tooltip>
	)
}

/**
 * The shelf as one table and the decision on one row in a dialog. The calls are
 * injected (`decide`, `listPoints`) so the table is driven at its props in `bun test`.
 */
export function HubShelfTable({
	items,
	decide,
	listPoints,
	onDone,
}: {
	items: HubShelfItem[]
	decide: DecideOnHubShelf
	listPoints: () => Promise<ShelfPoint[]>
	onDone: () => void
}) {
	const { t } = useTranslate()
	const [deciding, setDeciding] = useState<HubShelfItem | null>(null)

	const columns = useMemo<ColumnDef<HubShelfItem>[]>(
		() => [
			{
				id: 'code',
				header: t('support.colParcel'),
				cell: ({ row: { original: p } }) => (
					<Link to={`/parcels/${p.parcelId}`} className='font-mono underline-offset-4 hover:underline'>
						{p.trackingCode || p.parcelId}
					</Link>
				),
			},
			{
				id: 'reasons',
				header: t('support.reasons'),
				cell: ({ row: { original: p } }) => (
					<div className='flex flex-wrap gap-1' data-testid='hub-shelf-reasons'>
						{p.reasons.map((r) => (
							<Badge
								key={r}
								variant='outline'
								className='border-amber-500/60 text-amber-700 dark:text-amber-400'
								data-reason={r}
							>
								{t(`hubShelfReason.${r}`) || r}
							</Badge>
						))}
					</div>
				),
			},
			{
				id: 'partner',
				header: t('support.colPartner'),
				cell: ({ row: { original: p } }) => p.partnerName ?? <span className='text-muted-foreground'>{t('support.ownerless')}</span>,
			},
			{
				id: 'point',
				header: t('support.destination'),
				cell: ({ row: { original: p } }) => p.destinationPointCode ?? <span className='text-muted-foreground'>—</span>,
			},
			{
				id: 'decision',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row: { original: p } }) =>
					p.decision ? (
						<Button size='sm' variant='outline' onClick={() => setDeciding(p)} data-testid='hub-shelf-decide'>
							{t('hubShelfDecision.decide')}
						</Button>
					) : (
						<NotSupports item={p} />
					),
			},
		],
		[t],
	)

	const { table } = useDataTable({
		data: items,
		columns,
		rowCount: items.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		autoResetPageIndex: false,
		getRowId: (p) => p.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={<span data-testid='hub-shelf-empty'>{t('support.hubShelfEmpty')}</span>}
				row={{ attributes: (p) => ({ 'data-testid': `hub-shelf-${p.parcelId}` }) }}
			/>
			<DecideDialog
				item={deciding}
				decide={decide}
				listPoints={listPoints}
				onClose={() => setDeciding(null)}
				onDone={onDone}
			/>
		</>
	)
}

/**
 * The decision on one Parcel: «В пункт» (with the Point, and the Recipient and the
 * amount where the server says it needs them — `decision`) or «Вернуть партнёру»,
 * always with a comment. A refusal is said in words and the dialog stays.
 */
function DecideDialog({
	item,
	decide,
	listPoints,
	onClose,
	onDone,
}: {
	item: HubShelfItem | null
	decide: DecideOnHubShelf
	listPoints: () => Promise<ShelfPoint[]>
	onClose: () => void
	onDone: () => void
}) {
	const { t } = useTranslate()
	const [decision, setDecision] = useState<Decision | null>(null)
	const [points, setPoints] = useState<ShelfPoint[]>([])
	const [pointId, setPointId] = useState('')
	const [name, setName] = useState('')
	const [clientRef, setClientRef] = useState('')
	const [document, setDocument] = useState('')
	const [amount, setAmount] = useState('')
	const [comment, setComment] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [seeded, setSeeded] = useState<HubShelfItem | null>(null)
	if (item !== seeded) {
		setSeeded(item)
		setDecision(null)
		setPointId('')
		setName('')
		setClientRef('')
		setDocument('')
		setAmount('')
		setComment('')
		setError(null)
	}
	const needs = item?.decision ?? null

	useEffect(() => {
		if (decision !== 'to_point' || !needs) return
		listPoints().then(setPoints, () => setPoints([]))
	}, [decision, needs, listPoints])

	const submit = async () => {
		if (!item || !decision || !needs || busy) return
		const text = comment.trim()
		if (!text) {
			setError(t('hubShelfDecision.commentRequired'))
			return
		}
		const input: DecideInput = { parcelId: item.parcelId, decision, comment: text }
		if (decision === 'to_point') {
			if (pointId) input.pointId = pointId
			// The server says what is missing (`point_and_recipient_required`); a half-filled Recipient is not sent.
			if (name.trim() && clientRef.trim()) {
				input.recipient = {
					name: name.trim(),
					clientRef: clientRef.trim(),
					...(needs.documentType && document.trim() ? { document: document.trim() } : {}),
				}
			}
			if (amount.trim() !== '') {
				const value = Number(amount)
				if (!Number.isInteger(value) || value < 0) {
					setError(t('hubShelfDecision.amountInvalid'))
					return
				}
				input.amountOwed = value
			}
		}
		setBusy(true)
		setError(null)
		try {
			const answer = await decide(input)
			if (answer.outcome === 'refused') {
				setError(t(`hubShelfDecision.refused_${answer.reason}` as never))
				return
			}
			onClose()
			onDone()
		} catch (err) {
			setError(t('support.actionFailed', { message: err instanceof Error ? err.message : String(err) }))
		} finally {
			setBusy(false)
		}
	}

	const field = (id: string, labelText: string, value: string, set: (v: string) => void, type?: string) => (
		<div className='grid gap-1.5'>
			<Label htmlFor={`hub-shelf-${id}`}>{labelText}</Label>
			<Input
				id={`hub-shelf-${id}`}
				data-testid={`hub-shelf-${id}`}
				type={type}
				min={type === 'number' ? 0 : undefined}
				value={value}
				onChange={(e) => set(e.target.value)}
			/>
		</div>
	)

	return (
		<Dialog open={item !== null} onOpenChange={(open) => !open && !busy && onClose()}>
			<DialogContent className='sm:max-w-lg' data-testid='hub-shelf-dialog'>
				<DialogHeader>
					<DialogTitle>{t('hubShelfDecision.dialogTitle', { code: item?.trackingCode ?? '' })}</DialogTitle>
					<DialogDescription>
						{item?.reasons.map((r) => t(`hubShelfReason.${r}`) || r).join(' · ')}
					</DialogDescription>
				</DialogHeader>
				<div className='grid gap-4'>
					<div role='radiogroup' className='inline-flex w-fit items-center gap-0.5 rounded-lg bg-muted p-0.5'>
						{(['to_point', 'return_to_partner'] as const).map((d) => (
							<Button
								key={d}
								role='radio'
								size='sm'
								variant={decision === d ? 'outline' : 'ghost'}
								aria-checked={decision === d}
								onClick={() => setDecision(d)}
								data-testid={`hub-shelf-decision-${d}`}
							>
								{t(`hubShelfDecision.${d}`)}
							</Button>
						))}
					</div>
					{decision === 'to_point' && needs && (
						<div className='grid gap-3 sm:grid-cols-2'>
							<div className='grid gap-1.5'>
								<Label htmlFor='hub-shelf-point'>
									{needs.needsPoint ? t('hubShelfDecision.pointRequired') : t('hubShelfDecision.pointOptional')}
								</Label>
								<select
									id='hub-shelf-point'
									data-testid='hub-shelf-point'
									className={SELECT_CLASS}
									value={pointId}
									onChange={(e) => setPointId(e.target.value)}
								>
									<option value=''>
										{needs.needsPoint ? t('hubShelfDecision.choosePoint') : t('hubShelfDecision.ownPoint')}
									</option>
									{points.map((p) => (
										<option key={p.pointId} value={p.pointId}>
											{p.id}
										</option>
									))}
								</select>
							</div>
							{needs.needsAmount && field('amount', t('hubShelfDecision.amount'), amount, setAmount, 'number')}
							{needs.needsRecipient && field('name', t('hubShelfDecision.recipientName'), name, setName)}
							{needs.needsRecipient && field('client', t('hubShelfDecision.clientRef'), clientRef, setClientRef)}
							{needs.needsRecipient &&
								needs.documentType &&
								field(
									'document',
									t('hubShelfDecision.document', { type: t(`policies.documentType_${needs.documentType}` as never) }),
									document,
									setDocument,
								)}
						</div>
					)}
					{decision && (
						<Textarea
							aria-label={t('support.comment')}
							placeholder={t('hubShelfDecision.commentPlaceholder')}
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							data-testid='hub-shelf-comment'
						/>
					)}
					{error && (
						<p role='alert' className='text-sm text-destructive' data-testid='hub-shelf-error'>
							{error}
						</p>
					)}
				</div>
				<DialogFooter>
					<Button variant='outline' onClick={onClose} disabled={busy}>
						{t('support.cancel')}
					</Button>
					<Button onClick={submit} disabled={busy || !decision} data-testid='hub-shelf-submit'>
						{t('hubShelfDecision.submit')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
